import React from "react";
import PropTypes from "prop-types";
import { Form, Field } from "react-final-form";
import { TextInput } from "jl-design-system/components/text-input/TextInput";
import { Select } from "jl-design-system/components/select/Select";
import { Message } from "jl-design-system/components/message/Message";
import { PrimaryButton } from "jl-design-system/elements/button/Button";
import DisclaimerTooltip from "./DisclaimerTooltip";
import { dispatchAnalyticsEventWithAction } from "../../app/analytics/analytics.utils";
import { ENV_VARS } from "ENV_CONFIG";

// Local field configurations (migrated from jl-design-system/form/fields)
const TITLE = {
  name: 'title',
  id: 'title',
  type: 'select',
  label: 'Title',
  required: true,
  options: [
    { value: '', text: 'Please select' },
    { value: 'Mr', text: 'Mr' },
    { value: 'Mrs', text: 'Mrs' },
    { value: 'Miss', text: 'Miss' },
    { value: 'Ms', text: 'Ms' },
    { value: 'Mx', text: 'Mx' },
    { value: 'Dr', text: 'Dr' },
    { value: 'Other', text: 'Other' },
  ],
  validationMessage: 'Please select a title',
};

const FIRST_NAME = {
  name: 'firstName',
  id: 'firstName',
  type: 'text',
  label: 'First name',
  required: true,
  maxLength: 35,
  validationMessage: 'Please enter your first name',
};

const LAST_NAME = {
  name: 'lastName',
  id: 'lastName',
  type: 'text',
  label: 'Last name',
  required: true,
  maxLength: 35,
  validationMessage: 'Please enter your last name',
};

const EMAIL = {
  name: 'email',
  id: 'email',
  type: 'email',
  label: 'Email',
  required: true,
  validationMessage: 'Please enter your email address',
};

const getRequestConfig = (data) => {
  const maxUrlCharLength = 999;

  const sourceType = "RTB_JL_SIGNUP";
  const sourceUrl = window.location.href ? window.location.href.substring(0, maxUrlCharLength) : "https://www.johnlewis.com";
  const entity = "JL";
  const consentEmail = "Y";

  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json; charset=utf-8",
      appId: ENV_VARS.APP_ID,
    },
    body: JSON.stringify({
      ...data,
      sourceType,
      sourceUrl,
      entity,
      consentEmail,
    }),
  };
};

const getErrorMessage = (errorCode) => (errorCode === 400 ? "There are errors below,<br/>please correct them and try again" : "We're sorry, but something's not working right now, please try again later");

const dispatchAnalyticsError = (action, errorCode) => {
  if (!window.jlData) {
    window.jlData = {};
  }
  window.jlData.error = getErrorMessage(errorCode);
  dispatchAnalyticsEventWithAction(action);
};

export class SignupForm extends React.Component {
  constructor() {
    super();
    this.state = {
      submitFormErrorCode: false,
    };

    this.submitFormFields = this.submitFormFields.bind(this);
    this.handleOnChange = this.handleOnChange.bind(this);
    this.submitFailed = this.submitFailed.bind(this);
  }

  submitFormFields(fields) {
    const { onSuccess } = this.props;

    return new Promise((resolve, reject) => {
      const handleSuccess = () => {
        onSuccess();
        return resolve();
      };

      const handleError = (error) => {
        dispatchAnalyticsError("rtb.bannerError", error.status);
        this.props.hasError();
        this.setState({ submitFormErrorCode: error.status });
        return reject(error);
      };
      return fetch(`${ENV_VARS.REACT_APP_API_HOST}/appointments/bff/v1/permissions/`, getRequestConfig(fields))
        .then((response) => (response.status === 201 ? handleSuccess() : handleError(response)))
        .catch(handleError);
    });
  }

  submitFailed() {
    this.setState((state) => {
      const code = state.submitFormErrorCode || 400;
      dispatchAnalyticsError("rtb.bannerError", code);
      return { submitFormErrorCode: code };
    });

    this.props.hasError();
  }

  handleOnChange() {
    this.props.hasError(false);
    this.setState({ submitFormErrorCode: false });
  }

  render() {
    const { submitFormErrorCode } = this.state;

    // update the label and required of TITLE
    TITLE.label = "Title (optional)";
    TITLE.required = false;

    // match EMAIL field name to the service's prop for email
    EMAIL.name = "emailAddress";

    const formFields = [TITLE, FIRST_NAME, LAST_NAME, EMAIL];

    const validate = (values) => {
      const errors = {};
      formFields.forEach((field) => {
        if (field.required && !values[field.name]) {
          errors[field.name] = field.validationMessage || 'This field is required';
        }
        if (field.name === 'emailAddress' && values[field.name]) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(values[field.name])) {
            errors[field.name] = 'Please enter a valid email address';
          }
        }
      });
      return errors;
    };

    // We have to force scrolling to our <Message> component here because it didn't focus correctly on mobile when we had server errors.
    if (submitFormErrorCode) {
      const elemToScroll = document.querySelector('[class*="modal-content"]');
      if (elemToScroll) {
        elemToScroll.scrollTo(0, 0);
      }
    }

    return (
      <React.Fragment>
        <DisclaimerTooltip />

        {submitFormErrorCode && <Message type="error" title={getErrorMessage(submitFormErrorCode)} focusOnAlert refreshes={submitFormErrorCode} animation="shake" />}
        
        <Form
          onSubmit={this.submitFormFields}
          validate={validate}
          render={({ handleSubmit, submitting, hasValidationErrors }) => (
            <form onSubmit={handleSubmit} onChange={this.handleOnChange}>
              {formFields.map((field) => (
                <Field key={field.name} name={field.name}>
                  {({ input, meta }) => {
                    const showError = meta.touched && meta.error;
                    const FieldComponent = field.type === 'select' ? Select : TextInput;
                    
                    return (
                      <FieldComponent
                        {...input}
                        id={field.name}
                        labelText={field.label}
                        type={field.type || 'text'}
                        required={field.required}
                        error={showError ? meta.error : undefined}
                        options={field.options}
                      />
                    );
                  }}
                </Field>
              ))}
              <PrimaryButton
                type="submit"
                disabled={submitting}
                className="mt-4"
              >
                Sign up
              </PrimaryButton>
            </form>
          )}
        />
      </React.Fragment>
    );
  }
}

SignupForm.propTypes = {
  onSuccess: PropTypes.func.isRequired,
};
